import { Form } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLazyGetLegalEntitiesForSystemQuery } from '@app/api/LegalEntities';
import type { ModelPaymentAccount } from '@app/api/PaymentAccounts';
import {
  useLazyGetPaymentAccountByIdQuery,
  useUpdatePaymentAccountMutation,
} from '@app/api/PaymentAccounts';
import { BANK, PAYMENTS_ACCOUNTS_PATH } from '@app/constants';
import { ERROR, FRONT_WARNING, SUCCESS, WARNING } from '@app/constants/alerts';
import { useUnsavedData, useUserPrint } from '@app/hooks';
import { LeavePageContext, NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { ErrorResponse } from '@app/types/Errors';
import type { NLegalEntities } from '@app/types/LegalEntities';

import type { NAddPaymentsAccount } from '@pages/Finances/AddPaymentAccounts/AddPaymentsAccount.types';
import { initialInfoByLegalEntity } from '@pages/Finances/AddPaymentAccounts/data/initialInfoByLegalEntity';

const { useForm, useWatch } = Form;

export const useAddPaymentsAccount = () => {
  const { activeBranchId } = useUserPrint();

  const [getPaymentAccountById] = useLazyGetPaymentAccountByIdQuery();
  const [updatePaymentAccount] = useUpdatePaymentAccountMutation();
  const [getLegalEntitiesForSystem] = useLazyGetLegalEntitiesForSystemQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [form] = useForm();
  const [serverError, setServerError] = useState('');
  const [paymentAccount, setPaymentAccount] = useState<
    ModelPaymentAccount | undefined
  >();
  const [legalEntities, setLegalEntities] = useState<
    NLegalEntities.ModelItemEx[]
  >([]);
  const [payType, setPayType] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const editablePaymentAccountId = Number(searchParams.get('id'));

  const { showNotification } = useContext(NotificationContext);
  const {
    setNewData,
    prepareStartedData,
    checkIsDataUpdated,
    setInitialData,
    setInputData,
    setSelectorData,
    setCheckboxData,
  } = useContext(LeavePageContext);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchPaymentAccountData = async () => {
      setIsLoading(true);
      try {
        if (editablePaymentAccountId) {
          const result = await getPaymentAccountById({
            ActiveBranchId: activeBranchId,
            paymentAccountId: editablePaymentAccountId,
          }).unwrap();

          form.setFieldsValue({
            Name: result.Name,
            Description: result.Description,
            PayType: result.PayType,
            LegalEntityId: result.LegalEntityId,
            Bank: result.Bank,
            BankBik: result.BankBik,
            AccountCorrespondent: result.AccountCorrespondent,
            AccountChecking: result.AccountChecking,
            IsDefault: result.IsDefault,
          });

          setPaymentAccount(result);
          setPayType(result.PayType || '');

          prepareStartedData(result);

          const data = await getLegalEntitiesForSystem({
            ActiveBranchId: activeBranchId,
            includeDeleted: true,
          }).unwrap();

          setLegalEntities(
            data.filter(
              ({ Id, IsDeleted }) => !IsDeleted || result.LegalEntityId === Id
            )
          );
        } else {
          prepareStartedData(initialInfoByLegalEntity);

          const result = await getLegalEntitiesForSystem({
            ActiveBranchId: activeBranchId,
            includeDeleted: false,
          }).unwrap();

          setLegalEntities(result.filter(({ IsDeleted }) => !IsDeleted));
        }
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentAccountData();
  }, [
    activeBranchId,
    editablePaymentAccountId,
    form,
    getPaymentAccountById,
    getLegalEntitiesForSystem,
    showNotification,
    prepareStartedData,
  ]);

  useEffect(() => {
    if (payType === 'bank') {
      setNewData({
        Name: form.getFieldValue('Name'),
        LegalEntityId: form.getFieldValue('LegalEntityId'),
        PayType: form.getFieldValue('PayType'),
        Bank: form.getFieldValue('Bank'),
        Description: form.getFieldValue('Description'),
        IsDefault: form.getFieldValue('IsDefault'),
        BankBik: form.getFieldValue('BankBik'),
        AccountCorrespondent: form.getFieldValue('AccountCorrespondent'),
        AccountChecking: form.getFieldValue('AccountChecking'),
      });

      if (editablePaymentAccountId) {
        setInitialData({
          Name: paymentAccount?.Name,
          LegalEntityId: paymentAccount?.LegalEntityId,
          PayType: paymentAccount?.PayType,
          Bank: paymentAccount?.Bank,
          Description: paymentAccount?.Description,
          IsDefault: paymentAccount?.IsDefault,
          BankBik: paymentAccount?.BankBik,
          AccountCorrespondent: paymentAccount?.AccountCorrespondent,
          AccountChecking: paymentAccount?.AccountChecking,
        });
      } else {
        setInitialData({
          Name: undefined,
          LegalEntityId: undefined,
          PayType: undefined,
          Bank: undefined,
          Description: undefined,
          IsDefault: undefined,
          BankBik: undefined,
          AccountCorrespondent: undefined,
          AccountChecking: undefined,
        });
      }
    } else {
      setNewData({
        Name: form.getFieldValue('Name'),
        LegalEntityId: form.getFieldValue('LegalEntityId'),
        PayType: form.getFieldValue('PayType'),
        Description: form.getFieldValue('Description'),
        IsDefault: form.getFieldValue('IsDefault'),
      });

      if (editablePaymentAccountId) {
        setInitialData({
          Name: paymentAccount?.Name,
          LegalEntityId: paymentAccount?.LegalEntityId,
          PayType: paymentAccount?.PayType,
          Description: paymentAccount?.Description,
          IsDefault: paymentAccount?.IsDefault,
        });
      } else {
        setInitialData({
          Name: undefined,
          LegalEntityId: undefined,
          PayType: undefined,
          Description: undefined,
          IsDefault: undefined,
        });
      }
    }
  }, [
    form,
    payType,
    editablePaymentAccountId,
    paymentAccount?.AccountChecking,
    paymentAccount?.AccountCorrespondent,
    paymentAccount?.Bank,
    paymentAccount?.BankBik,
    paymentAccount?.Description,
    paymentAccount?.IsDefault,
    paymentAccount?.LegalEntityId,
    paymentAccount?.Name,
    paymentAccount?.PayType,
    setInitialData,
    setNewData,
  ]);

  const observedData = useWatch([], form);

  const { saveData } = useUnsavedData({
    data: observedData,
    skip: isLoading,
  });

  const submitForm: NAddPaymentsAccount.SubmitForm = useCallback(
    async ({
      LegalEntityId,
      Name,
      Description,
      PayType,
      Bank,
      BankBik,
      AccountChecking,
      AccountCorrespondent,
      IsDefault,
    }) => {
      if (activeBranchId) {
        try {
          setIsLoading(true);

          const isBankType = PayType === BANK;

          await updatePaymentAccount({
            ActiveBranchId: activeBranchId,
            data: {
              Id: paymentAccount ? paymentAccount.Id : 0,
              BranchId: activeBranchId,
              LegalEntityId,
              Name,
              Description,
              PayType,
              IsDeleted: paymentAccount?.IsDeleted || false,
              Bank: isBankType ? Bank : '',
              BankBik: isBankType ? BankBik : '',
              AccountChecking: isBankType ? AccountChecking : '',
              AccountCorrespondent: isBankType ? AccountCorrespondent : '',
              Balance: paymentAccount?.Balance || 0,
              IsDefault,
            },
          }).unwrap();

          saveData();
          showNotification(
            paymentAccount
              ? 'Платежный счет обновлен успешно'
              : 'Платежный счет создан успешно',
            SUCCESS
          );
          navigate(PAYMENTS_ACCOUNTS_PATH);
        } catch (error) {
          const { status, data: message } = error as ErrorResponse;

          if (status === 'ERROR') {
            showNotification(message, ERROR);
          } else if (status === 'WARNING') {
            setServerError(message);
          }
        } finally {
          setIsLoading(false);
        }
      }
    },
    [
      activeBranchId,
      navigate,
      showNotification,
      paymentAccount,
      saveData,
      updatePaymentAccount,
    ]
  );

  const handleCancel = () => {
    checkIsDataUpdated({
      path: PAYMENTS_ACCOUNTS_PATH,
      navigate,
    });
  };

  const handleDeletePaymentAccount = async () => {
    setIsLoading(true);

    const errorText = 'Недостаточно информации для выполнения операции';

    if (!activeBranchId || paymentAccount === undefined)
      return showNotification(errorText, FRONT_WARNING);

    try {
      await updatePaymentAccount({
        ActiveBranchId: activeBranchId,
        data: {
          Id: paymentAccount.Id,
          BranchId: activeBranchId,
          LegalEntityId: paymentAccount.LegalEntityId,
          Name: paymentAccount.Name,
          Description: paymentAccount.Description,
          PayType: paymentAccount.PayType,
          IsDeleted: true,
          Bank: paymentAccount.Bank,
          BankBik: paymentAccount.BankBik,
          AccountChecking: paymentAccount.AccountChecking,
          AccountCorrespondent: paymentAccount.AccountCorrespondent,
          Balance: paymentAccount.Balance,
        },
      }).unwrap();
      showNotification('Платежный счет удален успешно', SUCCESS);
      navigate(PAYMENTS_ACCOUNTS_PATH);
    } catch (error) {
      const { status, data: message } = error as ErrorResponse;

      if (status === 'ERROR') {
        showNotification(message, ERROR);
      } else if (status === 'WARNING') {
        setServerError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const initialValues = useMemo(
    () =>
      editablePaymentAccountId
        ? paymentAccount
        : {
            AccountChecking: '',
            AccountCorrespondent: '',
            Bank: '',
            BankBik: '',
            Description: '',
            IsDefault: false,
            LegalEntityId: null,
            Name: '',
            PayType: '',
          },
    [paymentAccount, editablePaymentAccountId]
  );

  const handlePayTypeChange = useCallback(
    (value: string) => {
      setPayType(value);
      setSelectorData({ value, fieldName: 'PayType' });

      form.setFieldsValue({
        BankBik: '',
        AccountCorrespondent: '',
        AccountChecking: '',
        Bank: '',
      });
    },
    [form, setSelectorData]
  );

  return {
    isLoading,
    form,
    submitForm,
    serverError,
    setServerError,
    editablePaymentAccountId,
    handleCancel,
    handleDeletePaymentAccount,
    legalEntities,
    payType,
    modalOpen,
    setModalOpen,
    setInputData,
    setSelectorData,
    setCheckboxData,
    initialValues,
    handlePayTypeChange,
  };
};
